"use client"

import { useState, useEffect, useRef } from "react"
import { Check, Minus, Info } from "lucide-react"
import { Button } from "@/components/ui/button"
import Head from "next/head"
import HrefLang from "@/components/HrefLang"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { useTranslations } from "next-intl"
import { useRouter } from "next/router"
import useMemberStore from "@/store/MemberStore"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

// Define the time value interface
interface TimeValue {
  value: number
  unitKey: string // e.g., "Title.hour", "Title.day"
}

// Define the plan types with locale-specific pricing
interface Plan {
  nameKey: string
  // USD price for Taiwanese users
  taiwanPrice: {
    usd: number
    annualUsd?: number
  }
  // USD price for international users (more expensive)
  internationalPrice: {
    usd: number
    annualUsd?: number
  }
  // Hard-coded NTD price (only shown to Taiwanese users)
  ntdPrice: number
  ntdAnnualPrice?: number
  descriptionKey: string
  buttonTextKey: string
  planType: string // Added planType to identify which plan is being purchased
}

// Define the feature category types
interface FeatureCategory {
  nameKey: string
  features: Feature[]
}

// Define the feature types (updated to include TimeValue and note)
interface Feature {
  nameKey: string
  descriptionKey?: string
  note?: string
  availability: {
    free: boolean | string | TimeValue
    personal: boolean | string | TimeValue
    professional: boolean | string | TimeValue
    business: boolean | string | TimeValue
  }
}

// Helper function to create time values
const createTimeValue = (value: number, unitKey: string): TimeValue => {
  return { value, unitKey }
}

// Plans data with different pricing for Taiwan vs International
const plans: Plan[] = [
  {
    nameKey: "Title.freePlan",
    taiwanPrice: {
      usd: 0,
      annualUsd: 0,
    },
    internationalPrice: {
      usd: 0,
      annualUsd: 0,
    },
    ntdPrice: 0,
    ntdAnnualPrice: 0,
    descriptionKey: "Description.freeUsage",
    buttonTextKey: "Action.registerNow",
    planType: "free",
  },
  {
    nameKey: "Title.personalPlan",
    taiwanPrice: {
      usd: 9.01, // Current USD price for Taiwan users
      annualUsd: 108.12,
    },
    internationalPrice: {
      usd: 15.99, // Higher USD price for international users
      annualUsd: 191.88,
    },
    ntdPrice: 299, // Hard-coded NTD price (only shown to Taiwan users)
    ntdAnnualPrice: 3588,
    descriptionKey: "Description.monthlyBilling",
    buttonTextKey: "Action.subscribePersonal",
    planType: "personal",
  },
  {
    nameKey: "Title.professionalPlan",
    taiwanPrice: {
      usd: 30.7, // Current USD price for Taiwan users
      annualUsd: 218.52,
    },
    internationalPrice: {
      usd: 49.99, // Higher USD price for international users
      annualUsd: 499.99,
    },
    ntdPrice: 999, // Hard-coded NTD price (only shown to Taiwan users)
    ntdAnnualPrice: 11988,
    descriptionKey: "Description.monthlyBilling",
    buttonTextKey: "Action.subscribeProfessional",
    planType: "professional",
  },
  //   {
  //     nameKey: "Title.businessPlan",
  //     taiwanPrice: {
  //       usd: 149.99,
  //       annualUsd: 1499.99
  //     },
  //     internationalPrice: {
  //       usd: 199.99,
  //       annualUsd: 1999.99
  //     },
  //     ntdPrice: 4650,
  //     ntdAnnualPrice: 46500,
  //     descriptionKey: "Description.monthlyBilling",
  //     buttonTextKey: "Action.subscribeBusiness",
  //     planType: "business"
  //   },
]

// Feature categories data with refactored time values
const featureCategories: FeatureCategory[] = [
  {
    nameKey: "Title.schedulePost",
    features: [
      {
        nameKey: "Title.postLimit",
        note: "Message.schedulePostLimit",
        availability: {
          free: "3",
          personal: "Title.unlimited",
          professional: "Title.unlimited",
          business: "Title.unlimited",
        },
      },
    ],
  },
  {
    nameKey: "Title.keywordTracking",
    features: [
      {
        nameKey: "Title.performanceChart",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.keywordCount",
        availability: {
          free: "1",
          personal: "3",
          professional: "6",
          business: "15",
        },
      },
      {
        nameKey: "Title.updateFrequency",
        availability: {
          free: createTimeValue(24, "Title.hour"),
          personal: createTimeValue(12, "Title.hour"),
          professional: createTimeValue(3, "Title.hour"),
          business: createTimeValue(1, "Title.hour"),
        },
      },
      {
        nameKey: "Title.historicalData",
        availability: {
          free: createTimeValue(3, "Title.day"),
          personal: createTimeValue(30, "Title.day"),
          professional: createTimeValue(90, "Title.day"),
          business: createTimeValue(90, "Title.day"),
        },
      },
      {
        nameKey: "Title.viewSearchedThreads",
        availability: {
          free: "Title.thirtyThreads",
          personal: "Title.unlimited",
          professional: "Title.unlimited",
          business: "Title.unlimited",
        },
      },
      {
        nameKey: "Title.threadExportCSV",
        availability: {
          free: false,
          personal: false,
          professional: true,
          business: true,
        },
      },
    ],
  },
  // {
  //   nameKey: "Title.keywordNewPostNotification",
  //   features: [
  //     {
  //       nameKey: "Title.keywordNewPostNotificationCount",
  //       availability: {
  //         free: "1",
  //         personal: "4",
  //         professional: "10",
  //         business: "20",
  //       },
  //     },
  //     {
  //       nameKey: "Title.keywordNewPostNotificationFrequency",
  //       availability: {
  //         free: createTimeValue(24, "Title.hour"),
  //         personal: createTimeValue(6, "Title.hour"),
  //         professional: createTimeValue(1, "Title.hour"),
  //         business: createTimeValue(1, "Title.hour"),
  //       },
  //     },
  //     {
  //       nameKey: "Title.emailNotification",
  //       availability: {
  //         free: "15封/月",
  //         personal: "500封/月",
  //         professional: "3000封/月",
  //         business: "500封/月",
  //       },
  //     },
  //     {
  //       nameKey: "Title.discordNotification",
  //       availability: {
  //         free: "15則/月",
  //         personal: "無上限",
  //         professional: "無上限",
  //         business: true,
  //       },
  //     },
  //     {
  //       nameKey: "Title.slackNotification",
  //       availability: {
  //         free: "15則/月",
  //         personal: "無上限",
  //         professional: "無上限",
  //         business: true,
  //       },
  //     },
  //     // {
  //     //   nameKey: "Title.lineNotification",
  //     //   availability: {
  //     //     free: false,
  //     //     personal: "50則/月",
  //     //     professional: "500則/月",
  //     //     business: true,
  //     //   },
  //     // },
  //   ],
  // },
  // {
  //   nameKey: "Title.keywordAutoResponse",
  //   features: [
  //     {
  //       nameKey: "Title.performanceOverview",
  //       availability: {
  //         free: true,
  //         personal: true,
  //         professional: true,
  //         business: true,
  //       },
  //     },
  //     {
  //       nameKey: "Title.monthlyReplyLimit",
  //       note: "Message.monthlyReplyLimit",
  //       availability: {
  //         free: "10",
  //         personal: "500",
  //         professional: "3000",
  //         business: "9999",
  //       },
  //     },
  //   ],
  // },
  {
    nameKey: "Title.teamCollaboration",
    features: [
      {
        nameKey: "Title.teamMemberCount",
        availability: {
          free: "1",
          personal: "1",
          professional: "10",
          business: "12",
        },
      },
    ],
  },
  {
    nameKey: "Title.threadsDataAnalysis",
    features: [
      {
        nameKey: "Title.dataCharts",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.likesRepostsReplies",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.threadsPerformanceHistory",
        note: "Message.threadsPerformanceHistory",
        availability: {
          free: createTimeValue(7, "Title.day"),
          personal: createTimeValue(60, "Title.day"),
          professional: "Title.unlimited",
          business: "Title.unlimited",
        },
      },
      {
        nameKey: "Title.postMetricsLoadMore",
        availability: {
          free: false,
          personal: true,
          professional: true,
          business: true,
        },
      },
    ],
  },
  {
    nameKey: "Title.threadsAccountAnalysis",
    features: [
      {
        nameKey: "Title.connectedAccountsCount",
        availability: {
          free: "1",
          personal: "2",
          professional: "6",
          business: "10",
        },
      },
      {
        nameKey: "Title.followerCountCharts",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.followerHistoryRecord",
        availability: {
          free: createTimeValue(3, "Title.day"),
          personal: createTimeValue(30, "Title.day"),
          professional: "Title.unlimited",
          business: "Title.unlimited",
        },
      },
      {
        nameKey: "Title.totalPostsStats",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.postLikesStats",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.postRepliesStats",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.postRepostsStats",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.postQuotesStats",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.consecutivePostingDaysStats",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.profileViewMeasurement",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.followerDemographicAnalysis",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
      {
        nameKey: "Title.postLikesHeatmap",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
    ],
  },
  {
    nameKey: "Title.usefulTools",
    features: [
      {
        nameKey: "Title.multiAccountPosting",
        availability: {
          free: true,
          personal: true,
          professional: true,
          business: true,
        },
      },
    ],
  },
]

export default function PricingPage() {
  const t = useTranslations("Pricing")
  const [isAnnual, setIsAnnual] = useState(false)
  const [isHeaderSticky, setIsHeaderSticky] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  // For testing, you can hardcode the locale, or use the actual router locale
  const { locale } = router
  // const locale = "zh-TW" // Hardcoded for testing, replace with router.locale in production
  const isTaiwanUser = locale === "zh-TW"
  const { fetchMember } = useMemberStore()
  const member = useMemberStore((state) => state.member)

  const handlePlanClick = (plan: Plan) => {
    // Get the appropriate URL based on plan type and user locale
    let itemUrl = ""

    if (plan.planType === "free") {
      // For free plan, redirect to signup
      router.push("/signup")
      return
    } else if (plan.planType === "personal") {
      itemUrl = isTaiwanUser
        ? process.env.NEXT_PUBLIC_PURCHASE_BASIC_TW_URL
        : process.env.NEXT_PUBLIC_PURCHASE_BASIC_EN_URL
    } else if (plan.planType === "professional") {
      itemUrl = isTaiwanUser
        ? process.env.NEXT_PUBLIC_PURCHASE_PRO_TW_URL
        : process.env.NEXT_PUBLIC_PURCHASE_PRO_EN_URL
    } else if (plan.planType === "business") {
      itemUrl = isTaiwanUser
        ? process.env.NEXT_PUBLIC_PURCHASE_BUSINESS_TW_URL
        : process.env.NEXT_PUBLIC_PURCHASE_BUSINESS_EN_URL
    }

    // Track analytics event with plan type
    // amplitude.track("Clicked_Purchase_Button", { planType: plan.planType })

    if (member) {
      const email = member.email
      const taiwanAddress = isTaiwanUser
        ? "checkout[billing_address][country]=TW"
        : ""
      const url = `${itemUrl}?${taiwanAddress}&checkout[email]=${email}&checkout[custom][user_id]=${member.id}`
      router.push(url)
    } else {
      router.push("/signup")
    }
  }

  useEffect(() => {
    fetchMember()
  }, [fetchMember])

  // Handle scroll to make header sticky
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current && tableRef.current) {
        const tableTop = tableRef.current.getBoundingClientRect().top
        setIsHeaderSticky(tableTop <= 0)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Get current USD price based on user locale and billing cycle
  const getCurrentUsdPrice = (plan: Plan) => {
    // Select price based on locale
    const priceSet = isTaiwanUser ? plan.taiwanPrice : plan.internationalPrice

    // Get price based on billing cycle
    if (isAnnual && priceSet.annualUsd) {
      return (priceSet.annualUsd / 12).toFixed(2)
    }
    return priceSet.usd.toFixed(2)
  }

  // Get current NTD price based on billing cycle (only for Taiwan users)
  const getCurrentNtdPrice = (plan: Plan) => {
    if (isAnnual && plan.ntdAnnualPrice) {
      return Math.round(plan.ntdAnnualPrice / 12).toLocaleString()
    }
    return plan.ntdPrice.toLocaleString()
  }

  // Check if a price is free (0)
  const isFree = (plan: Plan) => {
    const priceSet = isTaiwanUser ? plan.taiwanPrice : plan.internationalPrice
    return priceSet.usd === 0
  }

  // Render availability cell content - updated to handle TimeValue
  const renderAvailability = (availability: boolean | string | TimeValue) => {
    if (typeof availability === "boolean") {
      return availability ? (
        <Check className="h-5 w-5 text-emerald-500" />
      ) : (
        <Minus className="h-5 w-5 text-gray-300" />
      )
    }

    if (typeof availability === "string") {
      if (availability.startsWith("Title.")) {
        return <div className="text-sm text-gray-700">{t(availability)}</div>
      }
      return <div className="text-sm text-gray-700">{availability}</div>
    }

    // Handle TimeValue objects
    if (
      availability &&
      typeof availability === "object" &&
      "value" in availability
    ) {
      return (
        <div className="text-sm text-gray-700">
          {availability.value}{" "}
          {t(availability.unitKey, { count: availability.value })}
        </div>
      )
    }

    return null
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <Head>
        <title>{t("Meta.title")}</title>
        <meta name="og:title" content={t("Meta.title")} />
        <meta name="og:description" content={t("Meta.description")} />
        <meta name="description" content={t("Meta.description")} />
      </Head>
      <HrefLang />
      {/* Pricing Header */}
      <div
        ref={headerRef}
        className={`bg-white z-10 transition-all duration-200 ${
          isHeaderSticky
            ? "fixed top-0 left-0 right-0 shadow-md px-4 py-4"
            : "relative py-8"
        }`}>
        <div
          className={`max-w-6xl mx-auto ${isHeaderSticky ? "container" : ""}`}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{t("Title.plans")}</h2>
            <div className="flex items-center gap-3">
              {/* <span className="text-sm">{t("Title.annualBilling")}</span>
              <Switch
                id="annual-billing"
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
              />
              {isAnnual && (
                <span className="text-sm text-emerald-500 font-medium">
                  {t("Message.saveThreeMonths")}
                </span>
              )} */}
            </div>
          </div>

          <div className="grid grid-cols-4">
            <div className="col-span-1"></div>
            {plans.map((plan) => (
              <div
                key={plan.nameKey}
                className="col-span-1 text-center px-4 flex flex-col">
                <div>
                  <h3 className="text-lg font-semibold mb-2">
                    {t(plan.nameKey)}
                  </h3>
                  <div className="text-3xl font-bold mb-1">
                    ${getCurrentUsdPrice(plan)}
                    <span className="text-sm font-normal text-gray-500">
                      /{t("Title.month")}
                    </span>
                  </div>

                  {/* Show NTD price only for Taiwan users and non-free plans */}
                  <div
                    className={`text-md text-gray-600 mb-2 ${
                      isTaiwanUser && !isFree(plan) ? "visible" : "invisible"
                    }`}>
                    {!isFree(plan) ? (
                      <>
                        {t("Message.approximately")} NT${" "}
                        {getCurrentNtdPrice(plan)}
                        <span className="text-xs font-normal text-gray-500">
                          /{t("Title.month")}
                        </span>
                      </>
                    ) : (
                      <>&#8203;</> // Zero-width space to maintain height
                    )}
                  </div>

                  <p className="text-sm text-gray-500 mb-4">
                    {t(plan.descriptionKey)}
                  </p>
                </div>
                <div className="mt-auto">
                  <Button
                    className="w-full"
                    onClick={() => handlePlanClick(plan)}>
                    {t(plan.buttonTextKey)}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {isTaiwanUser && (
            <div className="text-center text-sm text-gray-500 mt-4 p-2 border border-gray-200 rounded-md bg-gray-50 mx-auto max-w-2xl">
              {t("Message.billingCurrencyNotice")}
            </div>
          )}
        </div>
      </div>

      {/* Spacer for when header becomes sticky */}
      {isHeaderSticky && <div className="h-[180px]"></div>}

      {/* Features Table */}
      <div ref={tableRef} className="mt-8">
        {featureCategories.map((category) => (
          <div key={category.nameKey} className="mb-6">
            <div className="bg-gray-100 p-3 mb-2">
              <h3 className="font-semibold text-gray-800">
                {t(category.nameKey)}
              </h3>
            </div>
            <div>
              {category.features.map((feature, index) => (
                <div
                  key={feature.nameKey}
                  className={`grid grid-cols-4 py-3 ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } border-b border-gray-100`}>
                  <div className="col-span-1 pl-3 flex items-center">
                    <span>{t(feature.nameKey)}</span>
                    {feature.note && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 ml-2 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t(feature.note)}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {renderAvailability(feature.availability.free)}
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {renderAvailability(feature.availability.personal)}
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {renderAvailability(feature.availability.professional)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* FAQ Section */}
      <div className="mt-16 mb-8">
        <h2 className="text-2xl font-bold text-center mb-8">
          {t("Title.faq")}
        </h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {/* Use dynamic FAQ data from the server instead of hardcoded values */}
            <AccordionItem value="item-0">
              <AccordionTrigger className="text-left">
                {t("Title.isPaymentSecure")}
              </AccordionTrigger>
              <AccordionContent>
                {t("Message.paymentSecurityInfo")}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500 mt-8">
        {t("Message.unsubscribeAnytime")}
      </div>
    </div>
  )
}

export async function getServerSideProps(context) {
  return {
    props: {
      messages: (await import(`@/messages/${context.locale}.json`)).default,
      // messages: (await import(`@/messages/zh-TW.json`)).default,
    },
  }
}
